import { useEffect } from "react";
import { Target, Building, Calculator, Wallet, GraduationCap, Shield, Phone } from "lucide-react";
import LeadForm from "../components/LeadForm";
import { SectionEyebrow, FadeInSection } from "../components/SharedSections";

const KAI_PHOTO = "https://static.wixstatic.com/media/8cad63_9e375d858dde499baa3b54b9699fb3dc~mv2.jpg/v1/fill/w_500,h_500,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/8cad63_9e375d858dde499baa3b54b9699fb3dc~mv2.jpg";

const FinancialPlanningPage = () => {
  useEffect(() => {
    document.title = "Financial Planning BC | Mayker Financial Group";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "Personalized financial planning for BC families and business owners. Retirement, estate, tax strategy and more.");
    }
    window.scrollTo(0, 0);
  }, []);

  const services = [
    { icon: <Target className="w-5 h-5" />, title: "Retirement Planning", desc: "When you want to retire, what it costs, and how to get there" },
    { icon: <Building className="w-5 h-5" />, title: "Estate Planning", desc: "Protecting your assets and ensuring your wishes are carried out" },
    { icon: <Calculator className="w-5 h-5" />, title: "Tax-Efficient Strategies", desc: "Keeping more of what you earn through smart planning" },
    { icon: <Wallet className="w-5 h-5" />, title: "Debt & Cash Flow Management", desc: "Getting ahead of debt and building positive cash flow" },
    { icon: <GraduationCap className="w-5 h-5" />, title: "Education Planning", desc: "RESP and saving for your children's future" },
    { icon: <Shield className="w-5 h-5" />, title: "Insurance Integration", desc: "Making sure your coverage supports your overall plan" }
  ];

  const steps = [
    { num: "1", title: "Discovery call", desc: "We get to know you, your goals, your timeline, and your concerns. 15 minutes, no paperwork." },
    { num: "2", title: "Deep dive", desc: "We analyze your full financial picture: income, assets, liabilities, insurance, and goals." },
    { num: "3", title: "Your plan", desc: "We deliver a clear, written financial plan with specific recommendations and next steps." },
    { num: "4", title: "Ongoing reviews", desc: "Life changes. We meet regularly to update your plan as your situation evolves." }
  ];

  return (
    <div data-testid="financial-planning-page">
      {/* Hero */}
      <section className="bg-[#0C1B33] pt-28 lg:pt-32 pb-16 lg:pb-20 px-4 lg:px-12 relative">
        <div className="hero-bottom-line" />
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow light>Financial Planning · British Columbia</SectionEyebrow>
            <h1 className="font-playfair text-3xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-3xl">
              A financial roadmap <em className="italic text-[#C9A84C]">built around your life.</em>
            </h1>
            <p className="text-base lg:text-lg text-white/60 leading-relaxed max-w-2xl mb-8">
              Whether you're planning for retirement, protecting your estate, or just trying to get ahead — we build a clear, personalized financial plan that gives you confidence in your future.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="#quote-form"
                className="inline-block bg-[#C9A84C] text-[#0C1B33] text-sm font-medium tracking-wide py-3.5 px-8 rounded hover:bg-[#E8CC80] transition-colors"
              >
                Start Your Plan →
              </a>
              <a 
                href="tel:7788361962"
                className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium py-3.5 px-4 transition-colors"
              >
                <Phone className="w-4 h-4" />
                Call 778-836-1962
              </a>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* What We Cover */}
      <section className="bg-[#FAF8F3] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow>What we cover</SectionEyebrow>
            <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-[#0C1B33] leading-tight mb-10 lg:mb-12">
              Comprehensive planning for every goal
            </h2>
          </FadeInSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((service, index) => (
              <FadeInSection key={index}>
                <div className="bg-white border border-black/5 rounded-lg p-5 h-full pillar-card">
                  <div className="w-10 h-10 bg-[#C9A84C]/10 rounded-lg flex items-center justify-center mb-3 text-[#C9A84C]">
                    {service.icon}
                  </div>
                  <h3 className="font-medium text-[#0C1B33] mb-1">{service.title}</h3>
                  <p className="text-sm text-[#6B7A99]">{service.desc}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* The Process */}
      <section className="bg-[#0C1B33] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow light>The process</SectionEyebrow>
            <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-white leading-tight mb-10 lg:mb-12">
              How we build your financial plan
            </h2>
          </FadeInSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <FadeInSection key={index}>
                <div className="text-center">
                  <div className="step-number bg-[#C9A84C] text-[#0C1B33] mx-auto mb-4">
                    {step.num}
                  </div>
                  <h3 className="font-playfair text-lg font-semibold text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Kai */}
      <section className="bg-[#F0EDE4] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <FadeInSection>
              <img 
                src={KAI_PHOTO}
                alt="Kai Simpson"
                className="w-full max-w-md mx-auto aspect-square object-cover object-top rounded-lg shadow-[0_24px_60px_rgba(0,0,0,0.15)]"
              />
            </FadeInSection>

            <FadeInSection>
              <SectionEyebrow>Your advisor</SectionEyebrow>
              <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-[#0C1B33] leading-tight mb-4">
                Meet Kai — your financial planning advisor
              </h2>
              <p className="text-[15px] text-[#6B7A99] leading-relaxed mb-4">
                Born and raised in Victoria, BC, Kai is passionate about helping people plan for the future with clarity and confidence. As an advisor with Mayker, he focuses on creating personalized strategies that reflect each client's goals and values.
              </p>
              <p className="text-[15px] text-[#6B7A99] leading-relaxed mb-6">
                Outside of work, Kai loves travel, sports, music and spending time with family — they remind him why financial peace of mind matters. His goal is to make the financial planning process simple, transparent and empowering for everyone he works with.
              </p>
              <a 
                href="/contact"
                className="inline-block bg-[#0C1B33] text-white text-sm font-medium tracking-wide py-3.5 px-8 rounded hover:bg-[#162647] transition-colors"
              >
                Book a call with Kai →
              </a>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* Lead Form */}
      <section id="quote-form" className="bg-[#0C1B33] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-xl mx-auto">
          <FadeInSection>
            <div className="bg-[#FAF8F3] rounded-xl p-6 lg:p-8 card-top-accent">
              <LeadForm 
                phoneRequired
                title="Start building your financial plan"
                subtitle="Book a free discovery call — no paperwork, no pressure."
                intentOptions={[
                  { value: "Retirement planning", label: "Retirement planning" },
                  { value: "Estate planning", label: "Estate planning" },
                  { value: "Tax strategy", label: "Tax strategy" },
                  { value: "Complete financial plan", label: "Complete financial plan" },
                  { value: "Not sure — need guidance", label: "Not sure — need guidance" },
                ]}
              />
            </div>
          </FadeInSection>
        </div>
      </section>
    </div>
  );
};

export default FinancialPlanningPage;
